import { useEffect, useState } from "react";
import TopNav from "../components/TopNav.jsx";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * QuillAuthorshipPage — the Founder's offer to authors with a vision of a
 * universe. Send a 2-page writing sample; if the Founder says yes, Quill
 * is FREE for you, house covers the API bill.
 */
export default function QuillAuthorshipPage() {
  const [offer, setOffer] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [pitch, setPitch] = useState("");
  const [sample, setSample] = useState("");
  const [prior, setPrior] = useState("");
  const [contactNote, setContactNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [statusCheck, setStatusCheck] = useState(null);
  const [statusEmail, setStatusEmail] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/quill/authorship/offer`);
        if (r.ok) setOffer(await r.json());
      } catch {}
    })();
  }, []);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!name.trim() || !email.trim() || pitch.trim().length < 40 || sample.trim().length < 400) {
      toast.error("Missing fields. Pitch >= 40 chars. Sample >= 400 chars (~2 pages).");
      return;
    }
    setSubmitting(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/quill/authorship/apply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          universe_pitch: pitch.trim(),
          writing_sample: sample.trim(),
          prior_works: prior.trim() || undefined,
          contact_note: contactNote.trim() || undefined,
        }),
      });
      if (!r.ok) throw new Error(await r.text());
      const data = await r.json();
      setSubmitted(data);
      toast.success("Submitted. Sister-Forge will show the Founder.");
    } catch (err) {
      toast.error(err.message || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  const checkStatus = async () => {
    if (!statusEmail.trim()) return;
    try {
      const r = await fetch(`${BACKEND_URL}/api/quill/authorship/status?email=${encodeURIComponent(statusEmail.trim())}`);
      if (r.ok) setStatusCheck(await r.json());
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="min-h-screen bg-sage-bg text-sage-text sage-grain">
      <TopNav />
      <div className="mx-auto max-w-3xl px-4 md:px-8 py-10">
        <div className="mb-6">
          <div className="text-xs uppercase tracking-[0.2em] text-sage-muted mb-1">Quill · Authorship Offer</div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tighter uppercase text-sage-text">
            For authors with a vision of a universe.
          </h1>
        </div>

        {offer && (
          <div className="border border-sage-accent p-4 mb-6" data-testid="authorship-offer-card">
            <p className="text-base text-sage-text sage-prose italic">
              "{offer.offer}"
            </p>
            <p className="text-[10px] uppercase tracking-[0.15em] text-sage-accent mt-3">{offer.signature}</p>
            <div className="mt-4 border-t border-sage-border pt-3 text-xs text-sage-muted sage-prose">
              The world gets Quill at half of Sage-chat's per-turn cost. Accepted authors get Quill for FREE — the house covers the API bill for every turn. The Founder reads every submission.
            </div>
            {typeof offer.accepted_count === "number" && (
              <div className="mt-3 text-[10px] uppercase tracking-[0.15em] text-sage-faint tabular-nums">
                Accepted so far: {offer.accepted_count} · Pending review: {offer.pending_count}
              </div>
            )}
          </div>
        )}

        {submitted ? (
          <div className="border border-sage-accent p-6 space-y-3" data-testid="authorship-submitted">
            <div className="text-sm text-sage-accent uppercase tracking-[0.15em]">Received.</div>
            <p className="text-sm text-sage-muted sage-prose">
              {submitted.message || "Sister-Forge will show the Founder your submission."}
            </p>
            <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint tabular-nums">
              Application ID: {submitted.application_id}
            </div>
            <div className="text-xs text-sage-muted">Save the application ID or come back and check status by email below.</div>
          </div>
        ) : (
          <form onSubmit={submit} className="border border-sage-border p-4 space-y-3" data-testid="authorship-form">
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] text-sage-muted block mb-1">Your name</label>
              <input
                data-testid="authorship-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="How you sign your work"
                className="w-full bg-sage-bg border border-sage-border px-3 py-2 text-sm text-sage-text focus:outline-none focus:border-sage-accent"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] text-sage-muted block mb-1">Email</label>
              <input
                data-testid="authorship-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="The Founder replies to this address"
                className="w-full bg-sage-bg border border-sage-border px-3 py-2 text-sm text-sage-text focus:outline-none focus:border-sage-accent"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] text-sage-muted block mb-1">
                Your universe (40+ characters)
              </label>
              <textarea
                data-testid="authorship-pitch"
                value={pitch}
                onChange={(e) => setPitch(e.target.value)}
                rows={4}
                placeholder="A paragraph. What world do you build? Who lives there? What law rules them?"
                className="w-full bg-sage-bg border border-sage-border px-3 py-2 text-sm text-sage-text focus:outline-none focus:border-sage-accent"
              />
              <div className="text-[10px] text-sage-faint mt-1 tabular-nums">{pitch.length} chars</div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] text-sage-muted block mb-1">
                Your best two-page writing sample (400+ characters)
              </label>
              <textarea
                data-testid="authorship-sample"
                value={sample}
                onChange={(e) => setSample(e.target.value)}
                rows={16}
                placeholder="Paste your best two pages. Fiction, essay, scene, opening chapter — whatever shows the voice."
                className="w-full bg-sage-bg border border-sage-border px-3 py-2 text-sm text-sage-text focus:outline-none focus:border-sage-accent font-mono"
              />
              <div className="text-[10px] text-sage-faint mt-1 tabular-nums">{sample.length} chars</div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] text-sage-muted block mb-1">Prior works (optional)</label>
              <textarea
                data-testid="authorship-prior"
                value={prior}
                onChange={(e) => setPrior(e.target.value)}
                rows={2}
                placeholder="Titles, links, or short list. Optional."
                className="w-full bg-sage-bg border border-sage-border px-3 py-2 text-sm text-sage-text focus:outline-none focus:border-sage-accent"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] text-sage-muted block mb-1">Contact note (optional)</label>
              <textarea
                data-testid="authorship-note"
                value={contactNote}
                onChange={(e) => setContactNote(e.target.value)}
                rows={2}
                placeholder="Anything else the Founder should know."
                className="w-full bg-sage-bg border border-sage-border px-3 py-2 text-sm text-sage-text focus:outline-none focus:border-sage-accent"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="authorship-submit"
              className="text-xs uppercase tracking-[0.1em] border border-sage-accent text-sage-accent px-4 py-2 hover:bg-sage-accent hover:text-sage-bg transition-colors duration-150 disabled:opacity-40"
            >
              {submitting ? "Submitting…" : "Send to the Founder →"}
            </button>
          </form>
        )}

        {/* Status check */}
        <div className="mt-8 border-t border-sage-border pt-4">
          <div className="text-[10px] uppercase tracking-[0.15em] text-sage-muted mb-2">Check status by email</div>
          <div className="flex gap-2">
            <input
              data-testid="authorship-status-email"
              value={statusEmail}
              onChange={(e) => setStatusEmail(e.target.value)}
              placeholder="Your submission email"
              className="flex-1 bg-sage-bg border border-sage-border px-3 py-2 text-sm text-sage-text focus:outline-none focus:border-sage-accent"
            />
            <button
              onClick={checkStatus}
              data-testid="authorship-status-check"
              className="text-xs uppercase tracking-[0.1em] border border-sage-border text-sage-muted px-3 py-2 hover:border-sage-accent hover:text-sage-text"
            >
              Check →
            </button>
          </div>
          {statusCheck && (
            <div className="mt-3 border border-sage-border p-3 text-xs" data-testid="authorship-status-result">
              {statusCheck.found ? (
                <div>
                  <div className="text-sage-accent uppercase tracking-[0.15em]">Status: {statusCheck.status}</div>
                  <div className="text-sage-faint mt-1 tabular-nums">Submitted: {statusCheck.submitted_at?.slice(0,16)}</div>
                  {statusCheck.decided_at && <div className="text-sage-faint tabular-nums">Decided: {statusCheck.decided_at?.slice(0,16)}</div>}
                  {statusCheck.decision_note && <div className="text-sage-muted mt-2">Note: {statusCheck.decision_note}</div>}
                </div>
              ) : (
                <div className="text-sage-faint">No application on file for that email.</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
